import './RoadProfile.scss';
import { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import { Button } from 'primereact/button';
import { useDispatch } from 'react-redux';
import { ButtonState } from '../../../shared/button-state/ButtonState';
import { setButtonState } from '../../../../state/features/buttonStateSlice';
import { AnkaScalable } from '../easy-pano-imports';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dialog } from 'primereact/dialog';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../../../state/hooks';
import { CustomizeRestApi } from '../../../../util/restapi/customize';
import { setCustomize } from '../../../../state/features/customizeSlice';
import { ValidateRestApi } from '../../../../util/restapi/validate';
import { setShowLoading } from '../../../../state/features/loadingSlice';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

interface Props {
    data: any[]
}

interface Point {
    x: number;
    y: number;
    lat: number;
    lon: number;
    alt: number;
}

export const RoadProfile = (props: Props) => {
    const svgContainerRef = useRef<any>();
    let minY: any = 0;
    let maxY: any = 0;
    let x: d3.ScaleLinear<number, number> = d3.scaleLinear();
    let y: d3.ScaleLinear<number, number> = d3.scaleLinear();
    let svg: any = null;
    let MOUSE_DATA: any = {
        selectChart: null,
        firstIndex: undefined,
        index: undefined,
        isMouseDown: false,
        isSelectionActive: false,
        onCTRLKeyDown: false,
        dragStart: null
    }
    let svgChart: any = null;
    let context: CanvasRenderingContext2D
    let scaleX: d3.ScaleLinear<number, number> = x
    let scaleY: d3.ScaleLinear<number, number> = y
    let transform: d3.ZoomTransform = d3.zoomIdentity;
    let pdf: any;
    let margins = {
        top: 10,
        right: 30,
        bottom: 30,
        left: 60
    };
    let chartWidth = 700 - margins.left - margins.right;
    let chartHeight = 350 - margins.top - margins.bottom;
    let canvasChart: d3.Selection<HTMLCanvasElement, unknown, HTMLElement, any>;

    const dispatch = useDispatch();
    const { t } = useTranslation();
    const finalizedSelectionsRef = useRef<Point[]>([]);
    const visiblePointsCache = useRef<{
        points: Point[],
        transform: string,
        width: number,
        height: number
    }>();

    const buttonState: any = useAppSelector(state => state.buttonState.buttonState)
    const [isSelectionFinalized, setIsSelectionFinalized] = useState<boolean>(false);
    const [selectedPoint, setSelectedPoint] = useState<Point | null>(null);
    const [IsGraph, setIsGraph] = useState(true);
    const [MousePoint, setMousePoint] = useState<Point | null>(null);
    const [Min_y, setMin_y] = useState<number | null>(null);
    const [Max_y, setMax_y] = useState<number | null>(null);
    const radius = useAppSelector((state) => state.roadProfileData.radius);
    const profileGeoms = useAppSelector(state => state.roadProfileData.profileGeoms);
    const Panogl_Drag = useAppSelector((state) => state.roadProfileData.panogl);
    const [CurrentHeight, setCurrentHeight] = useState<string>('-');
    const [WKTlon1, setWKTlon1] = useState<number | null>(null);
    const [WKTlon2, setWKTlon2] = useState<number | null>(null);
    const [WKTlat1, setWKTlat1] = useState<number | null>(null);
    const [WKTlat2, setWKTlat2] = useState<number | null>(null);
    const [WKTalt1, setWKTalt1] = useState<number | null>(null);
    const [WKTalt2, setWKTalt2] = useState<number | null>(null);
    const [logoData, setLogoData] = useState<string>('');
    const [companyName, setCompanyName] = useState<string>('');
    const [UseImage, setIsUseImage] = useState(false);

    const getCustomize = async () => {
        dispatch(setShowLoading(true))

        try {
            const response = await CustomizeRestApi.customize();
            setLogoData(response.data.customize.logo);
            setCompanyName(response.data.customize.company_name);
            dispatch(setCustomize(response.data.customize));
        } catch (error) {
            dispatch(setShowLoading(false))
        } finally {
            dispatch(setShowLoading(false));
        }
    }

    const licenseValidate = async () => {
        try {
            await ValidateRestApi.validate()
        } catch (error) { }
    }

    useEffect(() => {
        getCustomize()
        licenseValidate()
    }, [])

    useEffect(() => {
        // Give time for Dialog content to mount
        const timer = setTimeout(() => {

            if (buttonState !== ButtonState.ROAD_PROFILE) {
                handleHide()
            }

            if (svgContainerRef.current && props.data?.length) {
                // Clear previous chart
                while (svgContainerRef.current.firstChild) {
                    svgContainerRef.current.removeChild(svgContainerRef.current.firstChild);
                }

                const { svgChart, canvasChart, context } = draw2(svgContainerRef.current, props.data);

                // Store references if needed
                return () => {
                    // Cleanup
                    svgChart.remove();
                    canvasChart.remove();
                };
            }
        }, 100);
        return () => clearTimeout(timer);

    }, [props.data, buttonState, radius, IsGraph, profileGeoms, svgContainerRef]);

    const getVisiblePoints = (
        data: Point[],
        scaleX: d3.ScaleLinear<number, number>,
        scaleY: d3.ScaleLinear<number, number>
    ) => {
        const transformKey = `${scaleX.domain()}-${scaleY.domain()}`;

        // Return cached result if nothing changed
        if (visiblePointsCache.current &&
            visiblePointsCache.current.transform === transformKey &&
            visiblePointsCache.current.width === chartWidth &&
            visiblePointsCache.current.height === chartHeight) {
            return visiblePointsCache.current.points;
        }

        // Calculate new visible points
        const visiblePoints = data.filter(point => {
            const px = scaleX(point.x);
            const py = scaleY(point.y);
            return px >= 0 && px <= chartWidth && py >= 0 && py <= chartHeight;
        });

        // Cache the result
        visiblePointsCache.current = {
            points: visiblePoints,
            transform: transformKey,
            width: chartWidth,
            height: chartHeight
        };

        return visiblePoints;
    };

    const clusterPoints = (
        points: Point[],
        scaleX: d3.ScaleLinear<number, number>,
        scaleY: d3.ScaleLinear<number, number>,
        threshold: number = 5
    ) => {
        const clusters: {
            x: number,
            y: number,
            count: number,
            points: Point[]
        }[] = [];

        points.forEach(point => {
            const px = scaleX(point.x);
            const py = scaleY(point.y);

            // Find nearby cluster
            let foundCluster = clusters.find(cluster => {
                const dx = px - cluster.x;
                const dy = py - cluster.y;
                return Math.sqrt(dx * dx + dy * dy) < threshold;
            });

            if (foundCluster) {
                foundCluster.x = (foundCluster.x * foundCluster.count + px) / (foundCluster.count + 1);
                foundCluster.y = (foundCluster.y * foundCluster.count + py) / (foundCluster.count + 1);
                foundCluster.count++;
                foundCluster.points.push(point);
            } else {
                clusters.push({
                    x: px,
                    y: py,
                    count: 1,
                    points: [point]
                });
            }
        });

        return clusters;
    };

    const drawCanvasPoints = (
        context: CanvasRenderingContext2D,
        data: Point[],
        scaleX: d3.ScaleLinear<number, number>,
        scaleY: d3.ScaleLinear<number, number>,
        k: number = 1
    ) => {
        const baseRadius = 1.2;
        const radius = baseRadius * k;

        const currentSelections = finalizedSelectionsRef.current;

        // Use cached visible points
        const visiblePoints = getVisiblePoints(data, scaleX, scaleY);


        if (k < 0.5) {
            const clusters = clusterPoints(visiblePoints, scaleX, scaleY);

            clusters.forEach(cluster => {
                const radius = baseRadius * k * Math.sqrt(cluster.count);
                context.beginPath();
                context.fillStyle = '#ff9a03';
                context.arc(cluster.x, cluster.y, radius, 0, 2 * Math.PI);
                context.fill();
            });
        } else {
            // Draw finalized points (green)
            context.beginPath();
            context.fillStyle = '#00ff00';
            visiblePoints.forEach(point => {
                if (currentSelections.includes(point)) {
                    const px = scaleX(point.x);
                    const py = scaleY(point.y);
                    context.moveTo(px + radius, py);
                    context.arc(px, py, radius, 0, 2 * Math.PI, true);
                }
            });
            context.fill();

            // Draw non-finalized points (orange)
            context.beginPath();
            context.fillStyle = '#ff9a03';
            visiblePoints.forEach(point => {
                if (!currentSelections.includes(point)) {
                    const px = scaleX(point.x);
                    const py = scaleY(point.y);
                    context.moveTo(px + radius, py);
                    context.arc(px, py, radius, 0, 2 * Math.PI, true);
                }
            });
            context.fill();
        }
    };

    const draw2 = (svgContainer: HTMLDivElement, data: Array<{ x: number, y: number }>) => {
        const initialScale = data.length > 10000 ? 1 : 1;
        const initialTransform = d3.zoomIdentity.scale(initialScale);

        const container = d3.select("#ChartContainer")
            .style('position', 'relative');
        minY = d3.min(data, (d: any) => d.y)
        maxY = d3.max(data, (d: any) => d.y)

        setMin_y(minY);
        setMax_y(maxY);

        // Create SVG for axes
        svgChart = container.append('svg')
            .attr('width', chartWidth + margins.left + margins.right)
            .attr('height', chartHeight + margins.top + margins.bottom)
            .style('position', 'absolute')
            .append('g')
            .attr('transform', `translate(${margins.left}, ${margins.top})`);

        // Create canvas for rendering points
        canvasChart = container.append('canvas')
            .attr('width', chartWidth)
            .attr('height', chartHeight)
            .style('margin-left', margins.left + 'px')
            .style('margin-top', margins.top + 'px')
            .style('pointer-events', 'all')// Add this line
            .on('mousemove', mouseMove)
            .on('mouseover', onMouseUp)
            .on('mousedown', mouseDown)
            .on('contextmenu', handleRightClick)
            .attr('class', 'canvas-plot')
            .style('background', 'transparent')
            .style('z-index', '1')

        const makeXgridlines = d3.axisBottom(x)
            .tickSize(-chartHeight)
            .tickFormat(null)  // Remove labels

        const makeYgridlines = d3.axisLeft(y)
            .tickSize(-chartWidth)   // Use full chart width
            .tickFormat(null);  // Remove labels    

        svgChart.append('g')
            .attr('id', 'gridXLabel')
            .attr('transform', `translate(0, ${chartHeight})`)
            .style('stroke-dasharray', ('3,3'))
            .style('opacity', '0.5')
            .call(makeXgridlines)
            .selectAll('text').remove();

        svgChart.append('g')
            .attr('id', 'gridYLabel')
            .attr('transform', `translate(0, 0)`)
            .style('stroke-dasharray', ('3,3'))
            .style('opacity', '0.5')
            .call(makeYgridlines)
            .selectAll('text').remove();

        context = canvasChart.node()?.getContext('2d') as CanvasRenderingContext2D;

        // Establish scales
        x = d3.scaleLinear()
            .domain([d3.min(data, (d: any) => d.x), d3.max(data, d => d.x)])
            .range([0, chartWidth])
            .nice();

        y = d3.scaleLinear()
            .domain([
                minY - 2,  // 0.5 meters below min
                maxY + 2   // 0.5 meters above max
            ])
            .range([chartHeight, 0])
            .nice();

        // Create axes
        const xAxis: any = d3.axisBottom(x);
        const yAxis: any = d3.axisLeft(y);

        // Add X axis
        svgChart.append('g')
            .attr('class', 'x-axis')
            .attr('transform', `translate(0, ${chartHeight})`)
            .call(xAxis);

        // Add Y axis
        svgChart.append('g')
            .attr('class', 'y-axis')
            .call(yAxis);

        // Initial draw of points
        drawCanvasPoints(context, data as Point[],
            initialTransform.rescaleX(x),
            initialTransform.rescaleY(y),
            initialScale
        );
        // Zoom functionality
        const zoom = d3.zoom()
            .scaleExtent([initialScale, 20])
            .on('zoom', (d) => {

                transform = d3.event.transform;

                scaleX = initialTransform.rescaleX(x);
                scaleY = initialTransform.rescaleY(y);

                svgChart.select('#gridXLabel')
                    .call(makeXgridlines.scale(scaleX))
                    .selectAll('text').remove();

                svgChart.select('#gridYLabel')
                    .call(makeYgridlines.scale(scaleY))
                    .selectAll('text').remove();

                requestAnimationFrame(() => {
                    context.save();
                    draw(transform);
                    context.restore();
                });

            });

        // Attach zoom to canvas
        (canvasChart as any).call(zoom);


        if (data.length > 10000) {
            const initialScale = 15;
            const initialTransform = d3.zoomIdentity.scale(initialScale);

            // Cast the selection to Element type
            (d3.select(canvasChart.node() as Element) as any).call(zoom);
        }
        document.addEventListener('mouseup', onMouseUp)
        document.addEventListener('keydown', onKeyDown)
        document.addEventListener('keyup', onKeyUp)

        return { svgChart, canvasChart, context };
    };

    function draw(transform: d3.ZoomTransform) {
        scaleX = transform.rescaleX(x);
        scaleY = transform.rescaleY(y);

        svgChart.select('.x-axis').call(d3.axisBottom(scaleX));
        svgChart.select('.y-axis').call(d3.axisLeft(scaleY));

        context.clearRect(0, 0, chartWidth, chartHeight);

        drawCanvasPoints(context, props.data, scaleX, scaleY, transform.k);
    }

    const onDrag = () => {
        const firstIndex = MOUSE_DATA.firstIndex
        const currentIndex = MOUSE_DATA.index

        if (firstIndex == null) { return }

        if (firstIndex === currentIndex) { return }

        setIsSelectionFinalized(false);
        localStorage.setItem('isSelectionFinalized', JSON.stringify(false));
        const minIdx = Math.min(MOUSE_DATA.index, MOUSE_DATA.firstIndex);
        const maxIdx = Math.max(MOUSE_DATA.index, MOUSE_DATA.firstIndex) + 1;
        const newData = props.data.slice(minIdx, maxIdx);

        const startPoint = newData[0];
        const endPoint = newData[newData.length - 1];

        setWKTlon1(startPoint.lon);
        setWKTlon2(endPoint.lon);
        setWKTlat1(startPoint.lat);
        setWKTlat2(endPoint.lat);
        setWKTalt1(startPoint.alt);
        setWKTalt2(endPoint.alt);

        updateSelect(newData)
    }

    const updateSelect = (newData: Point[]) => {
        context.clearRect(0, 0, chartWidth, chartHeight);
        drawCanvasPoints(context, props.data, scaleX, scaleY, transform.k);
        // Draw active selection in red
        newData.forEach(point => {
            const px = scaleX(point.x);
            const py = scaleY(point.y);
            context.fillStyle = 'red';
            context.beginPath();
            context.arc(px, py, radius, 0, 2 * Math.PI);
            context.fill();
        });
    }

    useEffect(() => {
        if (WKTlon1 && WKTlon2 && WKTlat1 && WKTlat2 && WKTalt1 && WKTalt2) {
            const gdh = AnkaScalable.GeomDataHolder.fromWKT(`LINESTRING (${WKTlon1} ${WKTlat1} ${WKTalt1}, ${WKTlon2} ${WKTlat2} ${WKTalt2})`);
            if (Panogl_Drag) {
                const gdh = AnkaScalable.GeomDataHolder.fromWKT(`LINESTRING (${WKTlon1} ${WKTlat1} ${WKTalt1}, ${WKTlon2} ${WKTlat2} ${WKTalt2})`);
                Panogl_Drag?.getScalable()?.getMainSketchLayer().addGlobal(gdh);
                Panogl_Drag?.getScalable()?.getMainSketchLayer().refresh();
            }
        }
    }, [isSelectionFinalized, Panogl_Drag]);

    const setSelect = (b: any) => {
        if (MOUSE_DATA.onCTRLKeyDown !== b) {
            MOUSE_DATA.onCTRLKeyDown = b;
            if (b) {
                svg?.on('zoom', null);
            } else {
                return;
            }

        } else {
            return;
        }
    }

    useEffect(() => {
        const handleEscKey = (e: KeyboardEvent) => {
            if (e.code === 'Escape' && isSelectionFinalized) {
                cancelSelection();
            }
        };
        document.addEventListener('keydown', handleEscKey);
        return () => document.removeEventListener('keydown', handleEscKey);

    }, [isSelectionFinalized]);

    const onKeyDown = (e: any) => {
        if (e.keyCode === 17) {
            context.clearRect(0, 0, chartWidth, chartHeight);
            MOUSE_DATA.onCTRLKeyDown = true;
            MOUSE_DATA.isMouseDown = false;
            MOUSE_DATA.firstIndex = undefined;
            setSelect(true);

        }
    }

    const handleRightClick = (event: any) => {
        event?.preventDefault(); // Prevent default context menu

        if (MOUSE_DATA.isSelectionActive) {
            const minIdx = Math.min(MOUSE_DATA.index, MOUSE_DATA.firstIndex);
            const maxIdx = Math.max(MOUSE_DATA.index, MOUSE_DATA.firstIndex) + 1;
            const selectedData = props.data.slice(minIdx, maxIdx);

            finalizedSelectionsRef.current = selectedData;

            // First clear and redraw
            context.clearRect(0, 0, chartWidth, chartHeight);

            // Update state
            setSelectedPoint(selectedData[selectedData.length - 1]);
            setIsSelectionFinalized(true);

            // Immediately draw with new selections
            drawCanvasPoints(context, props.data, scaleX, scaleY, transform.k);

            // Reset selection state
            MOUSE_DATA.isSelectionActive = false;
            MOUSE_DATA.firstIndex = undefined;
            MOUSE_DATA.onCTRLKeyDown = false;
        }
    };

    const onKeyUp = (e: any) => {
        if (e.keyCode !== 17) { setSelect(false); }
    }

    const cancelSelection = () => {

        // Reset MOUSE_DATA
        MOUSE_DATA.isSelectionActive = false;
        MOUSE_DATA.firstIndex = undefined;
        MOUSE_DATA.index = undefined;
        MOUSE_DATA.onCTRLKeyDown = false;
        MOUSE_DATA.isMouseDown = false;

        // Reset selections state
        setIsSelectionFinalized(false);
    }

    useEffect(() => {
        return () => {
            document.removeEventListener('keydown', onKeyDown)
            document.removeEventListener('keyup', onKeyUp)
        }
    }, [])

    const mouseMove = (event: any) => {
        const canvasElement = d3.select('.canvas-plot').node() as HTMLCanvasElement;
        if (!canvasElement) return;

        const mousePos = d3.mouse(canvasElement);
        const mouseX = mousePos[0];

        // Get current transform from d3.event
        const transform = d3.event.transform || d3.zoomTransform(canvasElement);

        // Use transformed scales
        const scaleX = transform.rescaleX(x);
        const scaleY = transform.rescaleY(y);

        // Clear and redraw
        context.clearRect(0, 0, chartWidth, chartHeight);

        // Redraw points with transformed scales

        context.clearRect(0, 0, chartWidth, chartHeight);
        drawCanvasPoints(context, props.data, scaleX, scaleY, transform.k);

        // Draw vertical line using transformed x
        context.beginPath();
        context.strokeStyle = 'rgba(255, 255, 255, 0.8)';
        context.lineWidth = 3;
        context.moveTo(mouseX, 0);
        context.lineTo(mouseX, chartHeight);
        context.stroke();

        // Find closest point using transformed scale
        const xValue = (scaleX || x).invert(mouseX);
        const bisect = d3.bisector((d: Point) => d.x).left;
        const index = bisect(props.data, xValue);
        const point = props.data[index];

        // Draw circle using transformed coordinates
        context.beginPath();
        context.arc(
            (scaleX || x)(point?.x),
            (scaleY || y)(point?.y),
            5,
            0,
            2 * Math.PI
        );
        context.strokeStyle = 'white';
        context.lineWidth = 2;
        context.stroke();
        context.fillStyle = '#ff9a03';
        context.fill();


        const textX = scaleX(point?.x) + 10; // Offset from circle
        const textY = scaleY(point?.y) - 10; // Offset up from circle

        // Style text
        context.font = '12px Arial';
        context.fillStyle = 'white';
        context.textBaseline = 'bottom';

        // Draw text
        context.fillText(`${point?.y.toFixed(2)}m`, textX, textY);

        // Update current height state
        setCurrentHeight(point?.y.toFixed(3));
        setMousePoint(point);

        if (point && profileGeoms) {

            profileGeoms.setLabelAt({
                lon: point.lon,
                lat: point.lat,  // since lat and alt have the same values
                alt: point.alt
            });

        }


        if (MOUSE_DATA.onCTRLKeyDown) {
            MOUSE_DATA.index = index;
            if (MOUSE_DATA.isMouseDown) {
                if (MOUSE_DATA.firstIndex === undefined) {
                    MOUSE_DATA.firstIndex = index;
                }
                MOUSE_DATA.isSelectionActive = true;
            }
            onDrag();
        }
    };

    // Add to mouseDown
    const mouseDown = () => {
        if (MOUSE_DATA.onCTRLKeyDown) {
            MOUSE_DATA.isMouseDown = true;
            MOUSE_DATA.dragStart = d3.mouse(canvasChart.node() as HTMLCanvasElement);
        }
    }

    const onMouseUp = () => {
        MOUSE_DATA.isMouseDown = false;
        MOUSE_DATA.dragStart = null;
    }

    const ResetProfileData = () => {
        dispatch({ type: 'roadProfileData/setLon1', payload: null });
        dispatch({ type: 'roadProfileData/setLon2', payload: null });
        dispatch({ type: 'roadProfileData/setLat1', payload: null });
        dispatch({ type: 'roadProfileData/setLat2', payload: null });
        dispatch({ type: 'roadProfileData/setAltitude1', payload: null });
        dispatch({ type: 'roadProfileData/setAltitude2', payload: null });
        dispatch({ type: 'roadProfileData/setTimestamp', payload: null });
        dispatch({ type: 'roadProfileData/setRadius', payload: null });
        dispatch({ type: 'roadProfileData/setIsMap', payload: false });
    }

    const handleHide = () => {
        setIsGraph(false);
        dispatch(setButtonState(ButtonState.NONE));
        ResetProfileData();
    }

    const viewChange = (value: string) => {
        document.getElementsByClassName("home")[0].classList.remove('both');
        document.getElementsByClassName("home")[0].classList.remove('only-panorama');
        document.getElementsByClassName("home")[0].classList.remove('only-map');
        document.getElementsByClassName("home")[0].classList.add(value);
    }

    const handleDownloadPDF = () => {
        const chartContainer = document.getElementById('ChartContainer') as HTMLElement;
        const homeElement = document.getElementsByClassName("home")[0];
        dispatch(setShowLoading(true))

        if (homeElement) {
            // Clear all view states first
            homeElement.classList.remove('both');
            homeElement.classList.remove('only-map');
            homeElement.classList.remove('only-panorama');

            // Set only panorama view
            homeElement.classList.add('only-panorama');
            viewChange('only-panorama');

            const panoramaView = document.querySelector('.home.only-panorama') as HTMLElement;

            const img = new Image();
            img.src = logoData;

            const options = {
                scale: 2, // Better resolution
                useCORS: true,
                backgroundColor: '#17212f', // Match container background
                logging: true,
                style: {
                    '.road-profile-table': {
                        color: '#ffffff',
                        fontFamily: 'Arial',
                        fontSize: '12px'
                    },
                    '.p-datatable': {
                        border: '1px solid #ffffff',
                        borderCollapse: 'collapse'
                    },
                    'td, th': {
                        padding: '8px',
                        borderBottom: '1px solid #ffffff'
                    }
                }
            };

            const optionss = {
                scale: 2,
                useCORS: true,
                backgroundColor: '#17212f',
                logging: true
            };

            img.onload = () => {
                const canvas = document.createElement('canvas');
                canvas.width = img.width;
                canvas.height = img.height;
                const ctx = canvas.getContext('2d');
                ctx?.drawImage(img, 0, 0);

                const pngData = canvas.toDataURL('image/png');

                if (panoramaView) {

                    html2canvas(panoramaView, optionss).then(panoramaCanvas => {
                        const panoramaData = panoramaCanvas.toDataURL('image/png');

                        if (chartContainer) {

                            html2canvas(chartContainer, options).then(canvas => {

                                const imgData = canvas.toDataURL('image/png');
                                pdf = new jsPDF({
                                    orientation: 'p',
                                    unit: 'mm',
                                    format: 'a4',
                                    hotfixes: ['px_scaling'],
                                    compress: true,
                                    putOnlyUsedFonts: true
                                });

                                pdf.setFont('AbhayaLibre-Medium', 'normal', 'Identity-H');

                                const pageWidth = pdf.internal.pageSize.getWidth();
                                const pageHeight = pdf.internal.pageSize.getHeight();
                                const margin = 30;

                                // Logo and Company Name
                                const logoWidth = 20;
                                const logoHeight = 20;
                                const logoX = (pageWidth - logoWidth) / 2;
                                pdf.addImage(pngData, 'PNG', logoX, 10, logoWidth, logoHeight);

                                // Company name with spacing from logo
                                pdf.setFont("helvetica", "bold");
                                pdf.setFontSize(22);
                                pdf.text(companyName, pageWidth / 2, 40, { align: 'center' });

                                // Title with spacing from company name
                                pdf.setFontSize(26);
                                pdf.text(t(('TEXT.Road Profile Analysis')), pageWidth / 2, 55, { align: 'center' });

                                // Add Panorama
                                const panoramaWidth = pageWidth * 0.85;
                                const panoramaHeight = 80;
                                const panoramaX = (pageWidth - panoramaWidth) / 2;
                                const clipWidth = panoramaWidth * 0.5; // Show 70% of width
                                const clipX = panoramaX + (panoramaWidth * 0.15); // Start 15% from left

                                pdf.addImage(panoramaData, 'PNG', panoramaX, 65, panoramaWidth, panoramaHeight);


                                // Chart with adjusted spacing from title
                                const pdfWidth = pageWidth * 0.8;
                                const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
                                const xOffset = (pageWidth - pdfWidth) / 2;
                                const yOffset = 156;

                                pdf.addImage(imgData, 'PNG', xOffset, yOffset, pdfWidth, pdfHeight);


                                // Measurement Details with proper spacing from chart
                                pdf.setFont("helvetica", "bold");
                                pdf.setFontSize(15);

                                // For the heading text
                                const Eğim_Encoded = "data:image/jpeg;base64,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"
                                const I_Encoded = "data:image/jpeg;base64,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"

                                pdf.addImage(I_Encoded, 'png', 32, 78, 10, 5)

                                if (UseImage) {
                                    pdf.addImage(I_Encoded, 'png', 20, yOffset + pdfHeight + 5, 35, 9.5)
                                } else {
                                    pdf.text('Measurement Details', 20, yOffset + pdfHeight + 10);
                                }

                                // Data points with consistent spacing
                                const dataStartY = yOffset + pdfHeight + 25;
                                const lineSpacing = 8;

                                const tableData = [
                                    [`${t(('TEXT.Maximum Height(m)'))}`, `${Max_y?.toFixed(3) || '-'} (m)`],
                                    [`${t(('TEXT.Minimum Height(m)'))}`, `${Min_y?.toFixed(3) || '-'} (m)`],
                                    [`${t(('TEXT.Height(m)'))}`, `${CurrentHeight} (m)`],
                                    [`${t(('TEXT.Height Difference(m)'))}`, `${MousePoint && Min_y ? Math.abs(MousePoint.y - Min_y).toFixed(3) : '-'} (m)`],
                                    [UseImage ? Eğim_Encoded : "Slope", `${MousePoint && Min_y ? ((MousePoint.y - Min_y) / MousePoint.x * 100).toFixed(1) : '-'}%(m)`]

                                ];

                                // Set table position and styling
                                const startX = 20;
                                const startY = yOffset + pdfHeight + 15;
                                const cellPadding = 3;
                                const cellWidth = 60;
                                const cellHeight = 7;

                                pdf.setFontSize(10);
                                pdf.setFont("helvetica", "normal");

                                // Draw table
                                tableData.forEach((row, i) => {
                                    // Draw cells
                                    pdf.rect(startX, startY + (i * cellHeight), cellWidth, cellHeight);
                                    pdf.rect(startX + cellWidth, startY + (i * cellHeight), cellWidth, cellHeight);

                                    if (UseImage) {

                                        if (i === 4) { // Fourth row
                                            pdf.addImage(Eğim_Encoded, 'png', startX + cellPadding, startY + (i * cellHeight) + 2, 10, 3.5);
                                            pdf.text(row[1], startX + cellWidth + cellPadding, startY + (i * cellHeight) + 5);
                                        } else {
                                            pdf.text(row[0], startX + cellPadding, startY + (i * cellHeight) + 5);
                                            pdf.text(row[1], startX + cellWidth + cellPadding, startY + (i * cellHeight) + 5);
                                        }


                                    } else {

                                        pdf.text(row[0], startX + cellPadding, startY + (i * cellHeight) + 5);
                                        pdf.text(row[1], startX + cellWidth + cellPadding, startY + (i * cellHeight) + 5);

                                    }

                                });

                                pdf.save('road-profile.pdf');
                                dispatch(setShowLoading(false))

                            });

                        }
                    })


                };
            }

        }

    };

    return (
        <>
            <Dialog
                visible={IsGraph}
                className="container"
                header={
                    <div className="title">
                        <span style={{ color: 'white' }}>{t(('HEADER.ROAD PROFILE'))}</span>
                    </div>
                }
                onHide={handleHide}
                contentStyle={{ overflow: 'hidden' }}
                modal={false}
                resizable={true}
                blockScroll={false}
                draggable={true}
                closeOnEscape={false}
                maskClassName="custom-modal-mask"
                style={{ width: '788px' }}
            >
                <div className="body">
                    <div className="body-container">
                        <div
                            ref={svgContainerRef}
                            id="ChartContainer"
                            style={{
                                width: `${chartWidth + margins.left + margins.right}px`,
                                height: `${chartHeight + margins.top + margins.bottom}px`,
                                position: 'relative'
                            }}
                        />
                        <div className="info-container">
                            <DataTable value={[{
                                maxHeight: Max_y?.toFixed(3) || '-',
                                minHeight: Min_y?.toFixed(3) || '-',
                                height: isSelectionFinalized ? selectedPoint?.y.toFixed(3) : CurrentHeight,
                                heightDiff: isSelectionFinalized ?
                                    (selectedPoint && Min_y ? Math.abs(selectedPoint.y - Min_y).toFixed(3) : '-') :
                                    (MousePoint && Min_y ? Math.abs(MousePoint.y - Min_y).toFixed(3) : '-'),
                                slope: isSelectionFinalized ?
                                    (selectedPoint && Min_y ? `${((selectedPoint.y - Min_y) / selectedPoint.x * 100).toFixed(1)}%` : '-') :
                                    (MousePoint && Min_y ? `${((MousePoint.y - Min_y) / MousePoint.x * 100).toFixed(1)}%` : '-')
                            }]}
                                style={{ width: '650px', }}
                                className={`road-profile-table ${isSelectionFinalized ? 'finalized' : ''}`}
                            >
                                <Column field="maxHeight" header={t(('TEXT.Maximum Height(m)'))} className={isSelectionFinalized ? 'finalized' : ''} />
                                <Column field="minHeight" header={t(('TEXT.Minimum Height(m)'))} className={isSelectionFinalized ? 'finalized' : ''} />
                                <Column field="height" header={t(('TEXT.Height(m)'))} className={isSelectionFinalized ? 'finalized' : ''} />
                                <Column field="heightDiff" header={t(('TEXT.Height Difference(m)'))} className={isSelectionFinalized ? 'finalized' : ''} />
                                <Column field="slope" header={t(('TEXT.Slope(m)'))} className={isSelectionFinalized ? 'finalized' : ''} />
                            </DataTable>
                        </div>

                        <div className="button-container">
                            <Button
                                label={t(('TEXT.Download PDF'))}
                                onClick={handleDownloadPDF}
                            />
                        </div>
                    </div>
                </div>
            </Dialog>
        </>
    );
}

export default RoadProfile;